import { MixerHorizontalIcon } from '@radix-ui/react-icons';
import type { RowData } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ListSearch } from '@/lib/url-state';
import type { DataTableController, Density } from './use-data-table';

/** Column visibility and density, remembered per table in this browser. */
export function DataTableViewOptions<TData extends RowData, TSearch extends ListSearch>({
  controller,
}: {
  controller: DataTableController<TData, TSearch>;
}) {
  const { table } = controller;
  const hideable = table
    .getAllLeafColumns()
    .filter((column) => column.columnDef.meta?.hideable !== false && column.id !== 'select');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <MixerHorizontalIcon /> View
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Columns</DropdownMenuLabel>
        {hideable.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            checked={column.getIsVisible()}
            onCheckedChange={(value) => column.toggleVisibility(value === true)}
            onSelect={(e) => e.preventDefault()}
          >
            {column.columnDef.meta?.label ??
              (typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id)}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">Density</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={controller.density} onValueChange={(v) => controller.setDensity(v as Density)}>
          <DropdownMenuRadioItem value="comfortable">Comfortable</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="compact">Compact</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={controller.resetView}>Reset view</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
